import {
  MDSPanelHeaderProps,
  MDSPanelHeaderSlotName,
  MDSPanelHeaderRounded,
} from "@/mds/components/panel-header/panel-header.types";
import styled from "@emotion/styled";
import { getSlot } from "@/mds/utils/slots";
import { mdsPanelWidthsValue } from "@/design-system/foundations";

export const MDSPanelHeader = ({
  children,
  rounded = MDSPanelHeaderRounded.None,
  backgroundColor,
  bottomBorderColor,
  isSidePanel = false,
}: MDSPanelHeaderProps) => {
  const headingSlot = getSlot(children, MDSPanelHeaderSlotName.Heading);
  const subsectionSlot = getSlot(children, MDSPanelHeaderSlotName.Subsection);
  const actionsSlot = getSlot(children, MDSPanelHeaderSlotName.Actions);

  return (
    <Wrapper
      data-component="MDSPanelHeader"
      $rounded={rounded}
      $backgroundColor={backgroundColor}
      $bottomBorderColor={bottomBorderColor}
      $isSidePanel={isSidePanel}
    >
      <HeadingArea data-component="HeadingArea">
        <StyledHeading
          data-component="StyledHeading"
          $hasSubsection={!!subsectionSlot}
          $isSidePanel={isSidePanel}
        >
          {headingSlot}
        </StyledHeading>
      </HeadingArea>
      {subsectionSlot && <SubsectionArea>{subsectionSlot}</SubsectionArea>}
      {actionsSlot && <ActionsArea data-component="ActionsArea">{actionsSlot}</ActionsArea>}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $rounded: MDSPanelHeaderRounded;
  $backgroundColor?: string;
  $bottomBorderColor?: string;
  $isSidePanel?: boolean;
}>`
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: minmax(auto, max-content) minmax(auto, max-content) minmax(
      min-content,
      auto
    );
  grid-template-areas: "heading subsection actions";
  grid-column-gap: ${({ theme }) => theme.spacing["2"]};
  flex-shrink: 0;

  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ?? theme.colors.surface.paneHeader};

  align-items: ${({ $isSidePanel }) => ($isSidePanel ? "center" : "start")};
  padding: ${({ theme, $isSidePanel }) =>
    $isSidePanel
      ? `0 ${theme.spacing["3"]} 0 ${theme.spacing["5"]}`
      : `${theme.spacing["4"]} ${theme.spacing["4"]} 0 ${theme.spacing["4"]}`};
  min-height: ${({ theme }) => theme.spacing["10"]};
  width: 100%;

  border-bottom: ${({ theme, $bottomBorderColor, $isSidePanel }) =>
    `1px solid ${!$isSidePanel ? "transparent" : typeof $bottomBorderColor === "string" ? $bottomBorderColor : theme.colors.border.default}`};
  border-radius: ${({ theme, $rounded }) => {
    switch ($rounded) {
      case MDSPanelHeaderRounded.TopLeft:
        return `${theme.borderRadius.xlarge} 0 0 0`;
      case MDSPanelHeaderRounded.TopRight:
        return `0 ${theme.borderRadius.xlarge} 0 0`;
      default:
        return "0";
    }
  }};

  @container mds-panel (max-width: ${mdsPanelWidthsValue.headerBreakpoint}px) {
    grid-template-rows: minmax(auto, max-content) minmax(min-content, auto);
    grid-template-columns: auto auto;
    grid-template-areas:
      "heading actions"
      "subsection subsection";
  }
`;

const HeadingArea = styled.div`
  grid-area: heading;
  overflow: hidden;
  align-items: center;
`;

const SubsectionArea = styled.div`
  grid-area: subsection;
  overflow: hidden;
`;

const ActionsArea = styled.div`
  grid-area: actions;
  justify-self: end;
`;

const StyledHeading = styled.div<{ $hasSubsection: boolean; $isSidePanel: boolean }>`
  padding-inline: ${({ theme, $isSidePanel }) => ($isSidePanel ? 0 : theme.spacing["3"])};
  padding-bottom: ${({ theme, $isSidePanel }) => ($isSidePanel ? 0 : theme.spacing["3"])};

  @container mds-panel (max-width: ${mdsPanelWidthsValue.headerBreakpoint}px) {
    padding-bottom: ${({ $hasSubsection, theme, $isSidePanel }) =>
      $hasSubsection || $isSidePanel ? "0px" : theme.spacing["3"]};
  }
`;
